import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle, ArrowRight, CreditCard, HelpCircle } from 'lucide-react';

const CheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await apiService.get('/billing/subscription');
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Skeleton className="w-16 h-16 rounded-full mx-auto mb-6" />
            <Skeleton className="h-8 w-64 mx-auto mb-3" />
            <Skeleton className="h-5 w-48 mx-auto mb-8" />
            <Skeleton className="h-32 w-full mb-8" />
            <Skeleton className="h-12 w-full mb-3" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-foreground mb-3">
            Welcome to LocalizationHub!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Your subscription is now active. Your 14-day free trial has started.
          </p>

          {/* Subscription Details */}
          {subscription && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-8 text-left">
              <h2 className="font-semibold text-foreground mb-3 flex items-center">
                <CreditCard className="h-4 w-4 mr-2" />
                Subscription Details
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="font-medium text-foreground capitalize">{subscription.tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium text-green-600 capitalize">{subscription.status}</span>
                </div>
                {subscription.trialEnd && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trial Ends:</span>
                    <span className="font-medium text-foreground">
                      {new Date(subscription.trialEnd).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Next Billing:</span>
                  <span className="font-medium text-foreground">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-foreground mb-3">Next Steps</h3>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-xs font-medium">
                  1
                </span>
                <span>Create your first translation project</span>
              </li>
              <li className="flex items-start">
                <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-xs font-medium">
                  2
                </span>
                <span>Install the SDK or integrate with Shopify/WooCommerce</span>
              </li>
              <li className="flex items-start">
                <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-xs font-medium">
                  3
                </span>
                <span>Start translating your content automatically</span>
              </li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button asChild className="w-full">
              <Link to="/dashboard">
                Go to Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/billing">
                View Billing Details
              </Link>
            </Button>
          </div>

          {/* Support */}
          <p className="text-sm text-muted-foreground mt-6 flex items-center justify-center">
            <HelpCircle className="h-4 w-4 mr-1" />
            Need help getting started?{' '}
            <a href="#" className="text-primary hover:underline ml-1">
              Contact Support
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccess;
